import React from 'react';
import { Clock, TrendingUp, Handshake, Users, Award, FileText, BarChart, Settings, CheckCircle, XCircle, Info } from 'lucide-react'; // Added some relevant icons

const SLAPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const StatusBadge = ({ status, text }) => {
    const config = {
      implemented: { icon: CheckCircle, colors: 'bg-green-100 text-green-800 border-green-200' },
      partial: { icon: Info, colors: 'bg-blue-100 text-blue-800 border-blue-200' },
      notApplicable: { icon: XCircle, colors: 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    
    const { icon: Icon, colors } = config[status];

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors} whitespace-nowrap flex-shrink-0`}>
        <Icon className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-lime-200">
      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl text-gray-800 font-black tracking-wide">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <Handshake className="h-12 w-12 text-blue-600 mr-4" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sprint Reporter</h1>
              <h2 className="text-xl font-semibold text-blue-600">Service Level Agreement (SLA)</h2>
            </div>
          </div>
          <div className="text-center text-gray-600">
            <p>Last Updated: {currentDate}</p>
            <p>Version: 1.0</p>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Introduction
          </h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="text-blue-800">
              This Service Level Agreement (SLA) outlines the commitment of **Zykain** regarding the performance,
              availability, and support for the **Sprint Reporter** application. By using our Forge-based app,
              you acknowledge and agree to the terms set forth in this document. This SLA is designed to ensure
              clarity and establish mutual understanding of service expectations.
            </p>
          </div>
          <div className="mt-4 text-gray-700">
            <p>
              As a Forge-based application, Sprint Reporter primarily leverages Atlassian's robust and secure
              cloud infrastructure for its core functionality and data storage. Our commitments herein pertain
              to aspects directly under our control and management.
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* 1. Service Availability & Uptime */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-6 w-6 mr-2 text-green-600" />
              1. Service Availability & Uptime
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                Sprint Reporter's core availability is intrinsically tied to the **Atlassian Forge platform**'s
                uptime and performance. Atlassian provides its own Service Level Agreements for its cloud products,
                which directly impact the availability of Forge apps.
              </p>
              
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Our Commitment:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>We commit to designing and maintaining the Sprint Reporter app to fully utilize the
                    reliability and high availability offered by the Atlassian Forge platform.</li>
                <li>We will promptly address any app-specific issues that impact service availability,
                    aiming to minimize downtime.</li>
              </ul>

              <div className="bg-green-50 p-4 rounded-lg mt-4">
                <h5 className="font-medium text-green-800 mb-2">Key Considerations:</h5>
                <ul className="text-sm text-green-700 space-y-1">
                  <li><strong>Atlassian Forge Platform:</strong> The uptime of the underlying Forge platform
                      (including Jira Cloud) is governed by Atlassian's SLA, not directly by this document.</li>
                  <li><strong>Scheduled Maintenance:</strong> Atlassian may perform scheduled maintenance on
                      the Forge platform, which could temporarily affect app availability. We strive to provide
                      updates on such events when possible.</li>
                  <li><strong>Factors Beyond Our Control:</strong> Unavailability due to customer's internet
                      access, third-party network issues, or other factors outside of Atlassian's or our
                      reasonable control are excluded from this SLA.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. Performance Metrics */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart className="h-6 w-6 mr-2 text-purple-600" />
              2. Performance Metrics
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Report Generation Time</span>
                  <p className="text-sm text-gray-600">Reports should generate within typical Jira API response times.</p>
                </div>
                <StatusBadge status="implemented" text="Optimal" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">App Responsiveness</span>
                  <p className="text-sm text-gray-600">User interface elements should respond quickly to interactions.</p>
                </div>
                <StatusBadge status="implemented" text="High Priority" />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Performance Standards:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• **Report Generation:** While exact times vary based on Jira instance size and network conditions,
                      Sprint Reporter aims to generate most standard reports within seconds for typical data volumes.
                      Complex queries or very large data sets may take longer.</li>
                  <li>• **UI Responsiveness:** We continuously optimize our app's front-end for a smooth and responsive
                      user experience within the Atlassian Jira environment.</li>
                  <li>• **Error Rates:** We strive for minimal application-specific errors (bugs). Errors directly
                      attributable to the Sprint Reporter app will be treated as high-priority issues for resolution.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Technical Support */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-orange-600" />
              3. Technical Support
            </h3>
            <div className="space-y-4">
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
                <h5 className="font-medium text-orange-800 mb-2">Support Channels:</h5>
                <ul className="text-orange-700 text-sm space-y-1">
                  <li><strong>Email:</strong> For all support inquiries, bug reports, and feature requests,
                      please contact us at <a href="mailto:hello@zykain.com" className="underline font-semibold">hello@zykain.com</a>.</li>
                  <li><strong>Atlassian Marketplace:</strong> You can also raise issues via the
                      "Get Support" link on our Atlassian Marketplace listing.</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Response Time (Initial):</h6>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li><strong>High Priority (e.g., app completely unusable):</strong> Within 24 business hours</li>
                    <li><strong>Medium Priority (e.g., major feature broken):</strong> Within 48 business hours</li>
                    <li><strong>Low Priority (e.g., minor bug, feature request):</strong> Within 3-5 business days</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Resolution Time:</h6>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>Resolution times vary based on the complexity and severity of the issue. We aim for
                        the quickest possible resolution for all reported problems.</li>
                    <li>We will keep you informed of the progress for high and medium priority issues.</li>
                  </ul>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Support Hours:</h5>
                <p className="text-blue-700 text-sm">
                  Our support team operates primarily during Indian Standard Time (IST) business hours
                  (Monday to Friday, 12:00 PM - 8:00 PM IST), excluding public holidays in India.
                  High-priority issues may receive attention outside these hours.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Data Integrity & Security */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="h-6 w-6 mr-2 text-indigo-600" />
              4. Data Integrity & Security
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Data Storage</span>
                  <p className="text-sm text-gray-600">Configuration data stored securely using Atlassian Forge Storage.</p>
                </div>
                <StatusBadge status="implemented" text="Secure" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Data Minimization</span>
                  <p className="text-sm text-gray-600">Only essential configuration data is collected and stored.</p>
                </div>
                <StatusBadge status="implemented" text="Policy Compliant" />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Commitment to Data Integrity:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• We rely on Atlassian's robust security infrastructure for all data processed and stored
                      by the Forge app.</li>
                  <li>• Our app does not collect or store sensitive user data, Jira issue content, or
                      Atlassian user credentials/API tokens.</li>
                  <li>• For optional external integrations (e.g., Slack), we ensure that any configuration
                      data (like webhook URLs) is stored securely and transmitted via encrypted channels (HTTPS).</li>
                  <li>• In the event of a data incident related to our app (distinct from Atlassian platform
                      incidents), we will follow our incident response procedures and notify affected customers
                      as per applicable regulations and Atlassian guidelines.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 5. Exclusions & Limitations */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <XCircle className="h-6 w-6 mr-2 text-red-600" />
              5. Exclusions & Limitations
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                This SLA does not cover:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Downtime or performance issues caused by the Atlassian Jira Cloud platform or Forge platform itself.
                    Such issues are covered by Atlassian's own SLAs.</li>
                <li>Issues arising from customer-side infrastructure (e.g., internet connectivity, local network issues).</li>
                <li>Problems caused by third-party apps or integrations not explicitly managed by Sprint Reporter.</li>
                <li>Beta or experimental features of Sprint Reporter.</li>
                <li>Any misuse of the app or non-compliance with documented usage instructions.</li>
                <li>Scheduled maintenance of the Atlassian Forge platform or our own planned app updates (with prior notice, where possible).</li>
                <li>Force majeure events (e.g., natural disasters, acts of war, unforeseen catastrophic events).</li>
              </ul>
            </div>
          </section>

          {/* 6. Remedies */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-6 w-6 mr-2 text-blue-600" />
              6. Remedies
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                In the event that Sprint Reporter fails to meet the service levels outlined in this SLA for reasons
                directly attributable to our application and within our reasonable control, customers may contact
                our support team to discuss the issue.
              </p>
              <p className="text-gray-700 mb-4">
                As a Forge-based application, our direct control over infrastructure is limited. Therefore,
                monetary service credits or refunds are typically handled by Atlassian for platform-level
                downtime. For app-specific performance issues, our primary remedy will be dedicated efforts
                to diagnose and resolve the issue as quickly as possible.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> Since Sprint Reporter is built on Atlassian Forge, any significant
                  uptime or core performance issues are generally tied to the Atlassian platform's SLA.
                  This SLA focuses on the performance and support of the Sprint Reporter application itself.
                </p>
              </div>
            </div>
          </section>

          {/* 7. Changes to this SLA */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-gray-600" />
              7. Changes to this SLA
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                We may update this Service Level Agreement from time to time to reflect changes in our services,
                Atlassian's policies, or legal requirements. We will notify users of any significant changes
                by updating the "Last Updated" date at the top of this policy and, if necessary, through other
                communication channels (e.g., Atlassian Marketplace updates).
              </p>
              <p className="text-gray-700">
                Continued use of Sprint Reporter after any changes signifies your acceptance of the updated SLA.
              </p>
            </div>
          </section>

          {/* SLA Contact */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-blue-600" />
              8. Contact Information
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                For any questions or concerns regarding this Service Level Agreement, please contact us:
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Zykain Support</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> hello@zykain.com</li>
                  <li><strong>Website:</strong> zykain.com</li>
                </ul>
              </div>
            </div>
          </section>

        </div>

        {/* Final Note (before footer) */}
        <div className="bg-white p-6 mt-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                This Service Level Agreement reflects our commitment to providing a reliable and supported
                experience for Sprint Reporter App users, leveraging the robust foundation of Atlassian Forge.
              </p>
              <p className="text-gray-500 text-xs">
                © 2025 Sprint Reporter App. All rights reserved.
              </p>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                <strong>Important:</strong> As a Forge-based app, the fundamental platform reliability and
                infrastructure security are primarily managed by Atlassian. This SLA addresses the
                performance, support, and integrity specific to the Sprint Reporter application.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#16163F' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-black tracking-wide mb-4">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/sprint-reporter-faq" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                FAQs
              </a>
              <a 
                href="/support" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Support
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SLAPolicy;